
export enum View {
  DASHBOARD = 'DASHBOARD',
  CONTENT_PLANNER = 'CONTENT_PLANNER',
  WRITER = 'WRITER',
  AUDIT = 'AUDIT',
  TOOLS = 'TOOLS',
  AUTO_WRITER = 'AUTO_WRITER',
  AGENT = 'AGENT',
  COMPETITOR = 'COMPETITOR',
  CONTENT_GAP = 'CONTENT_GAP', 
  KEYWORD_RESEARCH = 'KEYWORD_RESEARCH',
  WALLET = 'WALLET',
  ADMIN_DASHBOARD = 'ADMIN_DASHBOARD',
  LIBRARY = 'LIBRARY',
  PUBLIC_VIEW = 'PUBLIC_VIEW',
  GSC_ANALYTICS = 'GSC_ANALYTICS',
  WEBSITES = 'WEBSITES',
  BRAND_VOICE = 'BRAND_VOICE'
}

export const COSTS = {
  WRITER_FLASH: 0.5,
  WRITER_PRO: 1.5,
  COMPETITOR_SPY: 2,
  CONTENT_GAP: 2.5,
  KEYWORD_RESEARCH: 1.5,
  AUDIT: 2.5,
  IMAGE_GEN: 0.1,
  OUTLINE_GEN: 0.5,
  GSC_ANALYSIS: 1.5,
  GSC_PREDICTION: 2.0,
  AGENT_SESSION: 5.0,
  TOOLS_RANK: 0.5,
  TOOLS_SCHEMA: 0.5,
  TOOLS_SOCIAL: 0.5,
  TOOLS_IMAGE_ANALYSIS: 0.5,
  TOOLS_PLAGIARISM: 1.5,
  INLINE_ACTION: 0.1,
  MINDMAP_EXPAND_NODE: 0.2,
  MINDMAP_ANALYZE_COVERAGE: 1.0,
};

export type UserRole = 'viewer' | 'editor' | 'admin';
export type UserStatus = 'active' | 'banned';
export type StorageTier = 'basic' | 'standard' | 'expert';
export type AIModel = 'gemini-4-argon' | 'gemini-3.8-flash' | 'gemini-3.1-pro-preview' | 'gemini-4.0-flash' | 'gemini-4.0-pro' | 'gemini-3.1-pro';
export type ContentLength = '500' | '1000' | '1500' | '2500';

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  role: UserRole;
  credits: number;
  totalDeposited?: number;
  storageTier: StorageTier;
  status: UserStatus;
  createdAt: string;
  lastLogin: string;
}

export interface InternalLink {
  keyword: string;
  url: string;
}

export interface SavedWebsite {
  id?: string;
  userId: string;
  url: string;
  username: string;
  appPassword: string;
  connectCode: string;
  context: string;
  internalLinks: InternalLink[];
  createdAt: string;
}

export interface WriterConfig {
  mode: 'create' | 'rewrite';
  topic: string;
  keyword: string;
  secondaryKeywords?: string;
  tone: string;
  type: string;
  audience: string;
  length: ContentLength;
  framework: 'none' | 'AIDA' | 'PAS' | 'FAB' | 'BAB' | 'SSS' | 'APP' | 'Listicle' | 'HowTo';
  useEEAT: boolean;
  expertContext: string;
  customOutline?: string;
  targetWebsiteId?: string;
  targetWebsiteContext?: string;
  internalLinks?: InternalLink[];
  model: AIModel;
  competitorContext?: string;
  sourceContent?: string;
  brandVoiceContext?: string;
  rewriteStyle?: string;
  brandVoiceSample?: string;
  language?: string;
  planId?: string;
  planStage?: ContentPlanStage;
  pillarTopic?: string;
  searchIntent?: string;
}

export interface WordPressConfig {
  url: string;
  username?: string;
  appPassword?: string;
  connectCode?: string;
  status: 'publish' | 'future' | 'draft';
}

export interface WpCategory {
  id: number;
  name: string;
  slug: string;
}

export interface SavedDocument {
  id?: string;
  userId: string;
  title: string;
  topic: string;
  content: string;
  config: WriterConfig;
  status: 'draft' | 'published';
  wpLink?: string | null;
  updatedAt: string;
  createdAt: string;
  wordCount: number;
  isPublic: boolean;
  shareId?: string | null;
  feedbacks?: DocumentFeedback[];
}

export interface Transaction {
  id: string;
  userId: string;
  userEmail: string;
  amount: number;
  credits: number;
  status: 'waiting_payment' | 'pending' | 'approved' | 'rejected';
  bankCode: string;
  content: string;
  proofImage?: string;
  createdAt: string;
  processedAt?: string; // Added for consistency
  processedBy?: string; // Added for consistency
}

export type TransactionStatus = 'waiting_payment' | 'pending' | 'approved' | 'rejected';

export interface DocumentFeedback {
  id: string;
  reviewerName: string;
  comment: string;
  status: 'approved' | 'changes_requested' | 'comment';
  createdAt: string;
}

export interface PersonaFeedback {
  persona: string;
  mood: string;
  satisfactionScore: number;
  verdict: string;
}

export interface UsageLog {
  id: string;
  userId: string;
  userEmail?: string;
  action: string;
  note: string;
  cost: number;
  timestamp: string;
}

export interface SystemStats {
  totalUsers: number;
  totalRevenue: number;
  pendingTransactions: number;
  totalAiRequests: number;
}

export interface SystemConfig {
  maintenanceMode: boolean;
  announcement: string;
  announcementType: 'info' | 'warning' | 'error';
  updatedAt: string;
  updatedBy: string;
}

export type ToastType = 'success' | 'error' | 'info' | 'warning';
export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
}

export interface AuditResult {
  score: number;
  readability: string;
  keywordDensity: any[];
  suggestions: string[];
  missingTopics: any[];
  userSimulation: PersonaFeedback[];
  aiSearchCompliance?: number;
  aiSearchTips?: string[];
}

export interface RankResult {
  keyword: string;
  rank: number;
  url: string;
  volume?: number;
  lastChecked: string;
  history: any[];
}

export interface TrackedKeyword {
  id?: string;
  userId: string;
  domain: string;
  keyword: string;
  location: string;
  currentRank: number;
  url?: string;
  volume?: number;
  lastChecked: string;
  history: any[];
}

export interface ImageSeoResult {
  altText: string;
  caption: string;
  filename: string;
}

export interface PlagiarismResult {
  score: number;
  status: string;
  matches: any[];
  analysis: string;
}

export interface QueueItem {
  id: string;
  topic: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  scheduledFor: string;
  logs: string[];
  wpLink?: string;
}

export interface SavedCampaign {
  id?: string;
  userId: string;
  name: string;
  topics: string[];
  writerConfig: WriterConfig;
  wpConfig: WordPressConfig;
  scheduleStart: string;
  postsPerDay: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  items?: QueueItem[]; // Added: Vital for resuming campaign progress visibility
}

export interface CompetitorAnalysisResult {
  keyword: string;
  difficultyLevel: string;
  searchIntent: string;
  topCompetitors: any[];
  strategy: any;
}

export interface ContentGapResult {
  gaps: any[];
  weaknesses: any[];
  strengths: any[];
}

export interface GscSite {
  siteUrl: string;
  permissionLevel: string;
}

export interface GscRow {
  keys: string[];
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface GscPrediction {
    keyword: string;
    page: string;
    prediction: string;
    confidence: 'High' | 'Medium' | 'Low';
    potentialClicks: number;
    timeframe: string;
}

export interface GscAnalysisResult {
  overallHealth: string;
  insights: any[];
  predictions?: GscPrediction[]; 
}

export interface AgentLog {
    timestamp: string;
    message: string;
    status: 'running' | 'success' | 'error' | 'info';
    data?: any;
}

export interface SEONewsItem {
    title: string;
    summary: string;
    source: string;
}

export interface KeywordData {
  keyword: string;
  monthlyVolume: string;
  intent: 'Informational' | 'Navigational' | 'Commercial' | 'Transactional' | 'Mixed';
  difficulty: number;
  cpc?: string;
  trend?: string;
  category?: string;
}
export type KeywordResearchResult = KeywordData[];

// --- CONTENT OS TYPES ---

export type ContentPlanStage = 'idea' | 'outline' | 'drafting' | 'review' | 'ready' | 'published' | 'decaying';
export type ContentPlanType = 'pillar' | 'cluster' | 'standalone';

export interface ContentPlanItem {
  id?: string;
  userId: string;
  siteId?: string;
  siteName?: string;
  topic: string;
  targetKeyword: string;
  secondaryKeywords?: string[] | string;
  searchVolume?: string;
  difficulty?: number;
  intent?: 'Informational' | 'Navigational' | 'Commercial' | 'Transactional' | 'Mixed';
  type: ContentPlanType;
  clusterId?: string; // Link to TopicCluster ID
  pillarId?: string; // If this is a cluster post, link to pillar ID
  pillarTopic?: string;
  stage: ContentPlanStage;
  dueDate?: string;
  docId?: string; // Linked SavedDocument ID
  liveUrl?: string; // WordPress or Public URL
  
  // GSC / Post-Publish Data
  clicks?: number;
  impressions?: number;
  position?: number;
  decayScore?: number; // 0-100 (high means decaying/traffic dropping)
  lastIndexedAt?: string;
  lastUpdatedDate?: string;
  
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TopicCluster {
  id?: string;
  userId: string;
  siteId?: string;
  siteName?: string;
  name: string;
  pillarKeyword: string;
  pillarTopic: string;
  clusterKeywords: string[];
  pillar?: { 
    topic: string; 
    keyword: string; 
    primaryKeyword?: string;
    secondaryKeywords?: string[]; 
    intent?: string; 
    volume?: string; 
    difficulty?: number 
  };
  satellites?: Array<{ 
    topic: string; 
    keyword: string; 
    primaryKeyword?: string;
    satelliteKeyword?: string;
    secondaryKeywords?: string[]; 
    intent?: string; 
    volume?: string; 
    difficulty?: number; 
    reason?: string 
  }>;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface KnowledgeDoc {
  id: string;
  title: string;
  content: string;
  type?: 'faq' | 'spec' | 'policy' | 'guide';
  addedAt: string;
}

export interface BrandVoiceProfile {
  id?: string;
  userId: string;
  siteId: string;
  siteName: string;
  brandName: string;
  toneVoice: string; // e.g., 'Academic & Deep Technical', 'Conversational & Friendly'
  targetAudience: string;
  personaDescription: string;
  vocabularyWhitelist?: string[]; // Preferred terms
  forbiddenWords?: string[]; // Banned clichés or competitor mentions
  mathJaxEnabled?: boolean;
  sampleExcerpt?: string;
  knowledgeDocs: KnowledgeDoc[];
  createdAt: string;
  updatedAt: string;
}

